<template>
  <div class="checkbox" :class="{ disabled }">
    <input
      :id="checkboxId"
      type="checkbox"
      class="checkbox__native"
      :disabled="disabled"
      :checked="isChecked"
      @change="onInputChange"
    />
    <label class="checkbox__label" :for="checkboxId">
      <i
        class="v-icon"
        :class="{
          'fas fa-check-square': isChecked,
          'far fa-square': !isChecked,
        }"
        :style="colorState"
        aria-hidden="true"
      />
      <span class="label-wrapper">
        <slot name="label">
          <ui-label
            :text="label"
            :help-tooltip="helpTooltip"
            :text-bold="false"
            :plan-limits="planLimits"
            :plan-limits-editable="planLimitsEditable"
          />
        </slot>
      </span>
    </label>
  </div>
</template>

<script setup lang="ts">
import { computed, useId } from 'vue';
import { isEqual } from 'lodash';
import { usePlanLimits, useColors, type PlanLimitsProp } from '@/composables';

const props = withDefaults(
  defineProps<{
    modelValue: unknown;
    color?: string;
    helpTooltip?: string;
    trueValue?: unknown;
    falseValue?: unknown;
    label?: string;
    disabled?: boolean;
    planLimits?: PlanLimitsProp;
    planLimitsEditable?: boolean;
  }>(),
  {
    helpTooltip: '',
    trueValue: true,
    falseValue: false,
    label: '',
    disabled: false,
    planLimitsEditable: false,
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: unknown];
  change: [value: unknown];
}>();

const $colors = useColors();
const checkboxId = useId();

const { showPlanLimitsIcon, handleClickPlanLimits } = usePlanLimits(
  () => props.planLimits,
  () => props.planLimitsEditable
);

const isChecked = computed(() => {
  const mv = props.modelValue;
  if (Array.isArray(mv)) {
    return mv.includes(props.trueValue);
  }
  return isEqual(mv, props.trueValue);
});

const colorState = computed(() => {
  if (props.disabled || !isChecked.value) return undefined;
  return { color: props.color || $colors.green };
});

function nextModelValue(nativeChecked: boolean): unknown {
  const mv = props.modelValue;
  if (Array.isArray(mv) && props.trueValue) {
    if (nativeChecked) {
      return mv.includes(props.trueValue) ? mv : [...mv, props.trueValue];
    }
    return mv.filter((item) => item !== props.trueValue);
  }
  return nativeChecked ? props.trueValue : props.falseValue;
}

function onInputChange(e: Event) {
  if (props.disabled) return;
  const el = e.target as HTMLInputElement;

  if (!props.planLimitsEditable && showPlanLimitsIcon.value) {
    el.checked = isChecked.value;
    handleClickPlanLimits();
    return;
  }

  const next = nextModelValue(el.checked);
  emit('update:modelValue', next);
  emit('change', next);
}
</script>

<style lang="scss" scoped>
.checkbox {
  display: flex;
  align-items: baseline;
  position: relative;
  cursor: pointer;
  user-select: none;
  padding-bottom: 5px;
  overflow: initial;
  width: fit-content;

  &.disabled {
    cursor: default;
  }

  .v-icon {
    color: var(--color-gray40);
    font-size: 16px;
    position: relative;
    margin-right: 6px;
  }
}

.checkbox__native {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  opacity: 0;
}

.checkbox__label {
  display: flex;
  align-items: baseline;
  cursor: pointer;
  margin: 0;
}

.label-wrapper {
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.ui-label {
  color: var(--color-graphite);
}

.checkbox.disabled .label-wrapper :deep(.ui-label) {
  color: var(--color-gray50);
}
</style>
